import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, QRCode, Space, Typography, Alert, Popconfirm, Spin, message } from 'antd';
import { SendOutlined, CheckCircleOutlined, DisconnectOutlined, ReloadOutlined } from '@ant-design/icons';
import axiosInstance from '../utils/axios';

const { Text, Paragraph } = Typography;

// Vincular el usuario del ERP con el bot de Telegram (servicio hss-bot).
// Si el backend no tiene el módulo del bot, el modal lo informa y no afecta al resto del panel.
const TelegramVinculo = ({ open, onClose }) => {
  const [estado, setEstado] = useState(null);
  const [link, setLink] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setEstado((await axiosInstance.get('/bot/telegram/estado')).data);
    } catch (e) {
      setError(e.response?.status === 404 ? 'El bot de Telegram no está disponible en este servidor.'
        : 'No se pudo consultar el estado de Telegram.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (open) { setLink(null); cargar(); }
  }, [open, cargar]);

  const generar = async () => {
    setCargando(true);
    try {
      setLink((await axiosInstance.post('/bot/telegram/codigo')).data);
    } catch (e) {
      message.error(e.response?.data?.error || 'No se pudo generar el link');
    } finally {
      setCargando(false);
    }
  };

  const desvincular = async () => {
    try {
      await axiosInstance.delete('/bot/telegram/vinculo');
      message.success('Telegram desvinculado');
      setLink(null);
      cargar();
    } catch {
      message.error('No se pudo desvincular');
    }
  };

  let contenido;
  if (error) {
    contenido = <Alert type="info" showIcon message={error} />;
  } else if (!estado) {
    contenido = <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>;
  } else if (!estado.configurado) {
    contenido = <Alert type="info" showIcon message="El bot de Telegram todavía no está configurado en el servidor." />;
  } else if (estado.vinculado) {
    contenido = (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert type="success" showIcon icon={<CheckCircleOutlined />}
          message={`Vinculado${estado.telegram_username ? ` como @${estado.telegram_username}` : ''}`}
          description={estado.desde ? `Desde el ${new Date(estado.desde + 'Z').toLocaleString('es-PY')}` : null} />
        <Paragraph style={{ marginBottom: 0 }}>
          Abrí <a href={`https://t.me/${estado.bot_username}`} target="_blank" rel="noreferrer">@{estado.bot_username}</a> en
          Telegram y escribí /ayuda para ver lo que podés hacer.
        </Paragraph>
        <Popconfirm title="¿Desvincular Telegram?" description="El bot dejará de responderte y de enviarte avisos."
          okText="Desvincular" cancelText="No" onConfirm={desvincular}>
          <Button danger icon={<DisconnectOutlined />}>Desvincular</Button>
        </Popconfirm>
      </Space>
    );
  } else if (link) {
    contenido = (
      <Space direction="vertical" align="center" style={{ width: '100%' }}>
        <QRCode value={link.link} size={200} />
        <Text type="secondary">Escaneá el código con el celular, o abrí el link desde la computadora.</Text>
        <Button type="primary" icon={<SendOutlined />} href={link.link} target="_blank">Abrir en Telegram</Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          El link sirve una sola vez y vence en {link.expira_en_minutos} minutos. En Telegram tocá «Iniciar».
        </Text>
        <Button icon={<ReloadOutlined />} onClick={cargar}>Ya lo vinculé</Button>
      </Space>
    );
  } else {
    contenido = (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Paragraph style={{ marginBottom: 0 }}>
          Con el bot <b>@{estado.bot_username}</b> podés consultar órdenes, vehículos y ubicaciones, completar órdenes
          y recibir avisos cuando te asignan una orden.
        </Paragraph>
        <Button type="primary" icon={<SendOutlined />} onClick={generar} loading={cargando}>
          Generar link de vinculación
        </Button>
      </Space>
    );
  }

  return (
    <Modal title={<Space><SendOutlined />Telegram</Space>} open={open} onCancel={onClose} footer={null} destroyOnClose>
      {contenido}
    </Modal>
  );
};

export default TelegramVinculo;
